import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { TranslationContext } from '../../../../assets/contexts/translationContext';
import {
  MIN_CARD_BALANCE_TOP_UP_USD,
  MIN_DEPOSIT_TRANSACTION_USD,
} from '../../../../assets/constants/depositData';
import {
  CopyIcon,
  RightArrowIcon,
} from '../../../../assets/icons/icons';
import { getCardById, getRemoteCardDepositAddress } from '../../../../assets/services/cards';
import useFundingQuote from '../../../../assets/hooks/useFundingQuote';
import {
  cancelDepositFlow,
  getDepositSession,
  startDepositFlow,
  subscribeDepositSession,
} from '../../../../assets/services/deposit';
import {
  buildQuickTopUpAmounts,
  formatFundingQuoteCommissionPercent,
  getDefaultFundingAsset,
  loadFundingAssets,
} from '../../../../assets/services/fundingAssets';
import {
  decimalEq,
  decimalGt,
  decimalGte,
  decimalPercentString,
  decimalToString,
  formatDecimal,
  parseDecimalInput,
  sanitizeDecimalInput,
} from '../../../../assets/utils/decimalValues';
import { getCurrencyIconUrl } from '../../../../assets/utils/currencyIcon';
import { triggerImpactHaptic } from '../../../../assets/utils/hapticFeedback';
import CardPage from '../../../CardPages/CardPage/CardPage';
import MiniPreloader from '../../../MiniPreloader/MiniPreloader';
import ScreenHeader from '../../../ScreenHeader/ScreenHeader';
import BottomActionButton from '../../../shared/BottomActionButton/BottomActionButton';
import CurrencyIcon from '../../../shared/CurrencyIcon/CurrencyIcon';
import CurrencyQrCode from '../../../shared/CurrencyQrCode/CurrencyQrCode';
import FundingAssetSheet from '../../../shared/FundingAssetSheet/FundingAssetSheet';
import DepositOperations from '../DepositOperations/DepositOperations';
import './DepositUSDT.css';

const STEP = {
  AMOUNT: 'amount',
  ADDRESS: 'address',
};

const SCREEN_TRANSITION = {
  duration: 0.28,
  ease: [0.22, 1, 0.36, 1],
};

function normalizeAmountInput(value, decimals = 2) {
  const sanitized = sanitizeDecimalInput(value, decimals);
  const [integerPart = '', fractionPart] = sanitized.split('.');
  const cappedIntegerPart = integerPart.slice(0, 8);

  return fractionPart == null ? cappedIntegerPart : `${cappedIntegerPart}.${fractionPart}`;
}

function formatExpectedAmount(amount, asset, decimals = 2) {
  const decimal = parseDecimalInput(amount);
  const currency = asset ? ` ${asset}` : '';

  if (!decimal || decimal.lte(0)) {
    return `0${currency}`;
  }

  return `${formatDecimal(decimal, decimals)}${currency}`;
}

function formatTopUpAmount(value, asset, decimals = 2) {
  const decimal = parseDecimalInput(value);
  const currency = asset ? ` ${asset}` : '';

  if (!decimal || decimal.lte(0)) {
    return `0.00${currency}`;
  }

  return `${formatDecimal(decimal, decimals, {
    minFractionDigits: Math.min(decimals, 2),
  })}${currency}`;
}

function formatProgressAssetValue(value, decimals = 2) {
  const decimal = parseDecimalInput(value);

  if (!decimal || decimal.lte(0)) {
    return '0';
  }

  return formatDecimal(decimal, decimals);
}

function formatCardBalanceAmount(value) {
  const decimal = parseDecimalInput(value);

  if (!decimal || decimal.lte(0)) {
    return '$0.00';
  }

  return formatDecimal(decimal, 2, {
    prefix: '$',
    minFractionDigits: 2,
  });
}

async function copyToClipboard(value) {
  const text = String(value ?? '');

  if (!text) {
    return false;
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall back to the legacy path below.
    }
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  textArea.style.pointerEvents = 'none';
  document.body.appendChild(textArea);
  textArea.select();

  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
}

function DepositQrCode({ iconUrl, label, value }) {
  return (
    <div className="deposit-usdt__qr" aria-label={label}>
      <CurrencyQrCode value={value} iconUrl={iconUrl} />
    </div>
  );
}

function DepositUSDT() {
  const navigate = useNavigate();
  const location = useLocation();
  const shouldReduceMotion = useReducedMotion();
  const { common, deposit, t } = useContext(TranslationContext);
  const [step, setStep] = useState(STEP.AMOUNT);
  const [fundingAssets, setFundingAssets] = useState([]);
  const [isFundingAssetsLoading, setIsFundingAssetsLoading] = useState(false);
  const [fundingAssetsError, setFundingAssetsError] = useState('');
  const [selectedFundingAsset, setSelectedFundingAsset] = useState(null);
  const [depositAddress, setDepositAddress] = useState(null);
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState('');
  const autoFilledAmountRef = useRef('');
  const pendingFundingAssetRef = useRef(null);
  const [isRequestingAddress, setIsRequestingAddress] = useState(false);
  const [isAssetSheetOpen, setIsAssetSheetOpen] = useState(false);
  const [receivedTopUpAssetAmount, setReceivedTopUpAssetAmount] = useState('0');
  const [depositOperations, setDepositOperations] = useState([]);
  const depositSession = getDepositSession();
  const sessionCardId = depositSession?.cardId;
  const depositCard = getCardById(sessionCardId);
  const depositCardLast4 = depositCard?.lastFour || depositCard?.last4 || '';
  const topUpCardTitle = depositCardLast4
    ? `${deposit.topUpCard} *${depositCardLast4}`
    : deposit.topUpCard;
  const returnPath = location.state?.returnPath || depositSession?.returnPath || '/home';
  const selectedAssetCode = selectedFundingAsset?.asset || '';
  const selectedCurrencyIconUrl = getCurrencyIconUrl(selectedFundingAsset);
  const selectedNetworkCode = selectedFundingAsset?.network || '';
  const selectedAssetDecimals = selectedFundingAsset?.decimalPlaces ?? 2;
  const selectedTopUpAddress = depositAddress?.address || '';
  const selectedMinimumAmount = MIN_CARD_BALANCE_TOP_UP_USD;
  const {
    quote: fundingQuote,
    quoteError: fundingQuoteError,
    isQuoteLoading: isFundingQuoteLoading,
    minimumQuote,
    minimumQuoteError,
    isMinimumQuoteLoading,
  } = useFundingQuote({
    amount,
    fundingAsset: selectedFundingAsset,
    minimumBalanceAmount: selectedMinimumAmount,
  });
  const {
    minimumQuote: minimumTransactionQuote,
    isMinimumQuoteLoading: isMinimumTransactionQuoteLoading,
  } = useFundingQuote({
    amount: '',
    fundingAsset: selectedFundingAsset,
    minimumBalanceAmount: MIN_DEPOSIT_TRANSACTION_USD,
  });
  const minimumFundingAmount = minimumQuote?.fundingAmount || '';
  const minimumTransactionFundingAmount = minimumTransactionQuote?.fundingAmount || '';
  const minimumTransactionFundingAmountValue = formatProgressAssetValue(
    minimumTransactionFundingAmount,
    selectedAssetDecimals,
  );
  const minimumTransferWarning = minimumTransactionFundingAmount && selectedAssetCode
    ? t('deposit.doNotSendLessAmount', {
      amount: minimumTransactionFundingAmountValue,
      asset: selectedAssetCode,
    })
    : deposit.doNotSendLess;
  const quickTopUpAmounts = useMemo(
    () => buildQuickTopUpAmounts(minimumFundingAmount, selectedAssetDecimals),
    [minimumFundingAmount, selectedAssetDecimals],
  );
  const defaultTopUpAmount = quickTopUpAmounts[0] || '';
  const cardBalanceTopUp = fundingQuote?.balanceAmount || '0';
  const expectedTopUpAmount = cardBalanceTopUp;
  const topUpFeePercent = fundingQuote
    ? formatFundingQuoteCommissionPercent(fundingQuote)
    : '—';
  const isQuoteLoading = isFundingQuoteLoading || (!amount && isMinimumQuoteLoading);
  const calculationError = fundingQuoteError || (!amount && minimumQuoteError);
  const topUpProgressPercent = decimalPercentString(receivedTopUpAssetAmount, amount);
  const expectedAmount = useMemo(
    () => formatExpectedAmount(amount, selectedAssetCode, selectedAssetDecimals),
    [amount, selectedAssetCode, selectedAssetDecimals],
  );
  const receivedTopUpLabel = formatProgressAssetValue(
    receivedTopUpAssetAmount,
    selectedAssetDecimals,
  );
  const expectedTopUpLabel = formatProgressAssetValue(amount, selectedAssetDecimals);
  const assetNetworkLabel = selectedAssetCode && selectedNetworkCode
    ? `${selectedAssetCode} (${selectedNetworkCode})`
    : selectedAssetCode || selectedNetworkCode;
  const handleAssetSheetOpen = useCallback(() => {
    pendingFundingAssetRef.current = null;
    setIsAssetSheetOpen(true);
  }, []);
  const handleAssetSheetClose = useCallback(() => {
    setIsAssetSheetOpen(false);
  }, []);
  const handleFundingAssetSelect = useCallback((asset) => {
    pendingFundingAssetRef.current = asset;
  }, []);
  const handleAssetSheetExitComplete = useCallback(() => {
    const asset = pendingFundingAssetRef.current;

    if (!asset) return;

    pendingFundingAssetRef.current = null;
    setSelectedFundingAsset(asset);
    setDepositAddress(null);
    setAmountError('');
  }, []);

  useEffect(() => {
    let isMounted = true;

    setIsFundingAssetsLoading(true);
    setFundingAssetsError('');

    loadFundingAssets()
      .then((assets) => {
        if (!isMounted) return;

        setFundingAssets(assets);
        setSelectedFundingAsset(
          (currentAsset) => currentAsset || getDefaultFundingAsset(assets),
        );
      })
      .catch(() => {
        if (!isMounted) return;
        setFundingAssetsError(common.failed || 'Failed');
      })
      .finally(() => {
        if (isMounted) {
          setIsFundingAssetsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [common.failed]);

  useEffect(() => {
    if (
      step !== STEP.AMOUNT
      || !selectedFundingAsset
      || !defaultTopUpAmount
      || !decimalGt(defaultTopUpAmount, '0')
    ) {
      return;
    }

    setAmount((currentAmount) => {
      if (currentAmount && currentAmount !== autoFilledAmountRef.current) {
        return currentAmount;
      }

      autoFilledAmountRef.current = defaultTopUpAmount;
      return defaultTopUpAmount;
    });
  }, [defaultTopUpAmount, selectedFundingAsset, step]);

  useEffect(() => {
    if (step !== STEP.ADDRESS) {
      return undefined;
    }

    const unsubscribe = subscribeDepositSession((nextSession) => {
      if (!nextSession) return;

      setReceivedTopUpAssetAmount(decimalToString(nextSession.receivedAssetAmount ?? '0'));
      setDepositOperations(nextSession.operations || []);

      if (nextSession.shouldOpenStatusFlow && nextSession.status === 'confirming') {
        navigate('/deposit/confirming', { replace: true, state: { returnPath } });
      }

      if (nextSession.shouldOpenStatusFlow && nextSession.status === 'success') {
        navigate('/deposit/success', { replace: true, state: { returnPath } });
      }

      if (nextSession.status === 'failed') {
        navigate('/deposit/failed', { replace: true, state: { returnPath } });
      }
    });

    setReceivedTopUpAssetAmount('0');
    setDepositOperations([]);
    if (!depositAddress?.address) {
      return unsubscribe;
    }

    startDepositFlow({
      cardId: sessionCardId,
      amount,
      asset: selectedFundingAsset.asset,
      network: selectedFundingAsset.network,
      address: depositAddress.address,
      addressId: depositAddress._id,
      memo: depositAddress.memo,
      minimumAmount: selectedMinimumAmount,
      balanceAmountUsd: cardBalanceTopUp,
      expectedAmountUsd: expectedTopUpAmount,
      returnPath,
    });

    return unsubscribe;
  }, [
    amount,
    cardBalanceTopUp,
    depositAddress,
    expectedTopUpAmount,
    navigate,
    returnPath,
    selectedFundingAsset,
    selectedMinimumAmount,
    sessionCardId,
    step,
  ]);

  const handleBack = () => {
    if (step === STEP.ADDRESS) {
      cancelDepositFlow();
      setStep(STEP.AMOUNT);
      return;
    }

    navigate('/home');
  };

  const handleAmountSubmit = async () => {
    if (!selectedFundingAsset) {
      setAmountError(deposit.selectFirst);
      return;
    }

    if (!sessionCardId) {
      toast.error(common.failed);
      return;
    }

    if (
      isFundingQuoteLoading
      || isMinimumTransactionQuoteLoading
      || !fundingQuote
      || !minimumFundingAmount
      || !minimumTransactionQuote
    ) {
      setAmountError(common.failed);
      return;
    }

    if (!decimalGte(cardBalanceTopUp, selectedMinimumAmount)) {
      setAmountError(t('deposit.minimumAmount', {
        amount: formatProgressAssetValue(minimumFundingAmount, selectedAssetDecimals),
        asset: selectedAssetCode,
      }));
      return;
    }

    setAmountError('');
    setIsRequestingAddress(true);

    try {
      const nextAddress = await getRemoteCardDepositAddress(sessionCardId, selectedFundingAsset);
      setDepositAddress(nextAddress);
      setStep(STEP.ADDRESS);
    } catch (error) {
      toast.error(error?.message || common.failed);
    } finally {
      setIsRequestingAddress(false);
    }
  };

  const handleCopyValue = async (value, label) => {
    triggerImpactHaptic('light');
    const isCopied = await copyToClipboard(value);

    if (isCopied) {
      toast(t('common.valueCopied', { label }), {
        autoClose: 1600,
        className: 'app-toast app-toast--copy',
      });
      return;
    }

    toast.error(common.failedToCopy);
  };

  const renderAmount = () => (
    <div className="deposit-usdt__screen deposit-usdt__screen--amount">
      <button
        type="button"
        className="deposit-usdt__asset-row"
        onClick={handleAssetSheetOpen}
      >
        <CurrencyIcon
          className="deposit-usdt__asset-icon"
          imageClassName="deposit-usdt__asset-icon-image"
          currency={selectedFundingAsset}
          code={selectedAssetCode}
        />
        <span className="deposit-usdt__asset-copy">
          <span className="deposit-usdt__asset-label">{deposit.currencyNetwork}</span>
          <span className="deposit-usdt__asset-value">
            {selectedFundingAsset
              ? `${selectedFundingAsset.asset} · ${selectedFundingAsset.network}`
              : isFundingAssetsLoading
                ? common.loading
                : deposit.selectCurrencyNetwork}
          </span>
        </span>
        <RightArrowIcon className="deposit-usdt__asset-chevron" />
      </button>

      {fundingAssetsError && (
        <p className="deposit-usdt__amount-error">{fundingAssetsError}</p>
      )}

      <AnimatePresence initial={false}>
        {selectedFundingAsset && (
          <motion.div
            className="deposit-usdt__amount-fields"
            initial={shouldReduceMotion
              ? { opacity: 0, transform: 'translate3d(0, 0px, 0)' }
              : { opacity: 0, transform: 'translate3d(0, 10px, 0)' }}
            animate={{ opacity: 1, transform: 'translate3d(0, 0px, 0)' }}
            exit={shouldReduceMotion
              ? { opacity: 0, transform: 'translate3d(0, 0px, 0)' }
              : { opacity: 0, transform: 'translate3d(0, -6px, 0)' }}
            transition={SCREEN_TRANSITION}
          >
            <label className="deposit-usdt__amount-card">
              <span className="deposit-usdt__amount-label">{deposit.topUpAmount}</span>
              <span className="deposit-usdt__amount-input-row">
                <input
                  type="text"
                  inputMode="decimal"
                  className="deposit-usdt__amount-input"
                  placeholder={defaultTopUpAmount || minimumFundingAmount || '25'}
                  value={amount}
                  onChange={(event) => {
                    autoFilledAmountRef.current = '';
                    setAmount(normalizeAmountInput(event.target.value, selectedAssetDecimals));
                    setAmountError('');
                  }}
                />
                <span className="deposit-usdt__amount-currency">{selectedAssetCode}</span>
              </span>
              <span
                className={
                  amountError
                    ? 'deposit-usdt__amount-error'
                    : 'deposit-usdt__amount-error deposit-usdt__amount-error--empty'
                }
              >
                {amountError
                  || (calculationError ? common.failed : '')
                  || (minimumFundingAmount
                    ? t('deposit.minimumAmount', {
                      amount: formatProgressAssetValue(minimumFundingAmount, selectedAssetDecimals),
                      asset: selectedAssetCode,
                    })
                    : '')}
              </span>
              <div className="deposit-usdt__quick-amounts" aria-label={deposit.topUpAmount}>
                {quickTopUpAmounts.map((quickAmount) => (
                  <button
                    key={quickAmount}
                    type="button"
                    className={
                      decimalEq(amount, String(quickAmount))
                        ? 'deposit-usdt__quick-amount deposit-usdt__quick-amount--selected'
                        : 'deposit-usdt__quick-amount'
                    }
                    onClick={() => {
                      autoFilledAmountRef.current = '';
                      setAmount(String(quickAmount));
                      setAmountError('');
                    }}
                  >
                    {quickAmount}
                  </button>
                ))}
              </div>
            </label>

            <div className="deposit-usdt__top-up-quote">
              <div className="deposit-usdt__top-up-quote-main">
                <span className="deposit-usdt__top-up-quote-label">{deposit.cardBalanceReceive}</span>
                <div
                  className="deposit-usdt__top-up-quote-value"
                  aria-live="polite"
                  aria-busy={isQuoteLoading}
                >
                  {isQuoteLoading
                    ? <MiniPreloader className="deposit-usdt__quote-loader" label={common.loading} />
                    : formatCardBalanceAmount(cardBalanceTopUp)}
                </div>
              </div>

              <div className="deposit-usdt__top-up-quote-row">
                <span>{deposit.topUpAmount}</span>
                <strong>{formatTopUpAmount(amount, selectedAssetCode, selectedAssetDecimals)}</strong>
              </div>
              <div className="deposit-usdt__top-up-quote-row">
                <span>{deposit.processingFee}</span>
                {isQuoteLoading
                  ? <MiniPreloader className="deposit-usdt__fee-loader" label={common.loading} />
                  : <strong>{topUpFeePercent}</strong>}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const renderAddress = () => (
    <div className="deposit-usdt__screen">
      <div className="deposit-usdt__qr-card">
        <DepositQrCode
          label={deposit.qrLabel}
          value={selectedTopUpAddress}
          iconUrl={selectedCurrencyIconUrl}
        />
        <span className="deposit-usdt__qr-caption">
          {t('deposit.depositAsset', { asset: assetNetworkLabel })}
        </span>
      </div>

      <button
        type="button"
        className="deposit-usdt__copy-card"
        onClick={() => handleCopyValue(amount, common.copiedLabels.amount)}
      >
        <span className="deposit-usdt__copy-label">
          {t('deposit.amountWithAsset', { asset: assetNetworkLabel })}
        </span>
        <span className="deposit-usdt__copy-row">
          <span className="deposit-usdt__copy-value">{expectedAmount}</span>
          <CopyIcon className="deposit-usdt__copy-icon" />
        </span>
      </button>

      <button
        type="button"
        className="deposit-usdt__copy-card"
        onClick={() => handleCopyValue(selectedTopUpAddress, common.copiedLabels.wallet)}
      >
        <span className="deposit-usdt__copy-label">
          {t('deposit.walletAddress', { asset: assetNetworkLabel })}
        </span>
        <span className="deposit-usdt__copy-row">
          <span className="deposit-usdt__copy-value deposit-usdt__copy-value--address">
            {selectedTopUpAddress}
          </span>
          <CopyIcon className="deposit-usdt__copy-icon" />
        </span>
      </button>

      <div className="deposit-usdt__progress-card">
        <div className="deposit-usdt__progress-header">
          <span>
            {deposit.received}
            {selectedAssetCode ? ` ${selectedAssetCode}` : ''}
          </span>
          <span>
            {receivedTopUpLabel} / {expectedTopUpLabel}
          </span>
        </div>
        <div className="deposit-usdt__progress-track" aria-hidden="true">
          <motion.span
            className="deposit-usdt__progress-value"
            initial={false}
            animate={{ transform: `scaleX(${topUpProgressPercent / 100})` }}
            transition={shouldReduceMotion
              ? { duration: 0 }
              : { duration: 0.32, ease: 'easeOut' }}
          />
        </div>
      </div>

      <DepositOperations operations={depositOperations} showCardProgress={false} />

      <div className="deposit-usdt__warning">
        {deposit.warning} {minimumTransferWarning}
      </div>
    </div>
  );

  return (
    <CardPage className="deposit-usdt">
      <div className="deposit-usdt__content">
        <ScreenHeader
          title={step === STEP.AMOUNT ? topUpCardTitle : deposit.topUpAddress}
          onBack={handleBack}
        />

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step}
            initial={shouldReduceMotion
              ? { opacity: 0, transform: 'translate3d(0, 0px, 0)' }
              : { opacity: 0, transform: 'translate3d(0, 12px, 0)' }}
            animate={{ opacity: 1, transform: 'translate3d(0, 0px, 0)' }}
            exit={shouldReduceMotion
              ? { opacity: 0, transform: 'translate3d(0, 0px, 0)' }
              : { opacity: 0, transform: 'translate3d(0, -8px, 0)' }}
            transition={SCREEN_TRANSITION}
          >
            {step === STEP.AMOUNT && renderAmount()}
            {step === STEP.ADDRESS && renderAddress()}
          </motion.div>
        </AnimatePresence>
      </div>

      {step === STEP.AMOUNT && (
        <BottomActionButton
          className="deposit-usdt__cta"
          disabled={
            !selectedFundingAsset
            || !amount
            || !fundingQuote
            || !minimumFundingAmount
            || !minimumTransactionQuote
            || isMinimumQuoteLoading
            || isMinimumTransactionQuoteLoading
            || isFundingQuoteLoading
            || isRequestingAddress
            || isFundingAssetsLoading
          }
          onClick={handleAmountSubmit}
        >
          {deposit.showAddress}
        </BottomActionButton>
      )}

      <AnimatePresence onExitComplete={handleAssetSheetExitComplete}>
        {isAssetSheetOpen && (
          <FundingAssetSheet
            assets={fundingAssets}
            selectedAsset={selectedFundingAsset}
            onSelect={handleFundingAssetSelect}
            onClose={handleAssetSheetClose}
          />
        )}
      </AnimatePresence>

    </CardPage>
  );
}

export default DepositUSDT;
